import React, { useState } from 'react';
import {
    Divider,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    styled,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Icon from '@mdi/react';
import {
    mdiFilterVariant,
    mdiMap,
    mdiTrayArrowDown,
    mdiViewComfy,
    mdiViewList,
} from '@mdi/js';

import { useAppDispatch } from '../../state/hooks';
import { openFilterPanel } from '../../state/features/filterSlice';
import ActiveFilters from '../Filters/ActiveFilters';

const classesPrefix = 'tableHeader';

const classes = {
    tableTitleContainer: `${classesPrefix}-tableTitleContainer`,
    tableTitleRow: `${classesPrefix}-tableTitleRow`,
    tableTitle: `${classesPrefix}-tableTitle`,
    headerIcon: `${classesPrefix}-headerIcon`,
    divider: `${classesPrefix}-divider`,
    appliedFiltersContainer: `${classesPrefix}-appliedFiltersContainer`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.tableTitleContainer}`]: {
            backgroundImage: 'url("/road_bg.svg")',
            backgroundRepeat: 'no-repeat',
            backgroundPositionX: 'right',
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.getContrastText(theme.palette.primary.main),
            padding: theme.spacing(1),
            flexShrink: 1,
        },
        [`& .${classes.tableTitleRow}`]: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        [`& .${classes.tableTitle}`]: {
            flexGrow: 1,
        },
        [`& .${classes.headerIcon}`]: {
            color: theme.palette.common.white,
            margin: `0px ${theme.spacing(0.5)}`,
            '&:hover': {
                backgroundColor: theme.palette.common.white,
                color: theme.palette.primary.main,
            },
            '&:disabled': {
                cursor: 'not-allowed',
                opacity: 0.75,
                color: theme.palette.grey[50],
            },
        },
        [`& .${classes.divider}`]: {
            borderColor: theme.palette.getContrastText(
                theme.palette.primary.main
            ),
        },
        [`& .${classes.appliedFiltersContainer}`]: {
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
        },
    };
});

const TableHeader = ({
    title,
    itemCount,
    loading,
    adjusting,
    expanded,
    toggleExpansion,
    handleDownload,
}: {
    title: string;
    itemCount: number;
    loading: boolean;
    adjusting?: boolean;
    expanded?: boolean;
    toggleExpansion?: (isExpanded: boolean) => void;
    handleDownload: (allColumns: boolean) => void;
}): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMdOrSmaller = useMediaQuery(theme.breakpoints.down('lg'));

    const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<HTMLElement | null>(null);

    const handleDownloadMenuClose = (): void => {
        setDownloadMenuAnchorEl(null);
    };

    const handleAllColumnsDownloadClick = (): void => {
        handleDownload(true);
        handleDownloadMenuClose();
    };

    const handleVisibleColumnsDownloadClick = (): void => {
        handleDownload(false);
        handleDownloadMenuClose();
    };

    return (
        <StyledDiv className={classes.tableTitleContainer}>
            <span className={classes.tableTitleRow}>
                <Typography variant='h3' className={classes.tableTitle}>
                    {title}
                </Typography>
                <Tooltip title='Export'>
                    <span>
                        <IconButton
                            onClick={(event): void => {
                                setDownloadMenuAnchorEl(event.currentTarget);
                            }}
                            className={classes.headerIcon}
                            disabled={loading || adjusting}
                            size={isMdOrSmaller ? 'small' : 'large'}
                        >
                            <Icon path={mdiTrayArrowDown} size={1} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Menu
                    anchorEl={downloadMenuAnchorEl}
                    id='download-menu'
                    open={Boolean(downloadMenuAnchorEl)}
                    onClose={handleDownloadMenuClose}
                    onClick={handleDownloadMenuClose}
                >
                    <MenuItem onClick={handleVisibleColumnsDownloadClick}>
                        CSV (Visible Columns)
                    </MenuItem>
                    <MenuItem onClick={handleAllColumnsDownloadClick}>
                        CSV (All Columns)
                    </MenuItem>
                </Menu>

                <Tooltip title='Filters'>
                    <span>
                        <IconButton
                            className={classes.headerIcon}
                            onClick={(): void => {
                                dispatch(openFilterPanel());
                            }}
                            disabled={adjusting}
                            size={isMdOrSmaller ? 'small' : 'large'}
                        >
                            <Icon path={mdiFilterVariant} size={1} />
                        </IconButton>
                    </span>
                </Tooltip>
                {
                    toggleExpansion &&
                    <Tooltip title={`${expanded ? 'Collapse Table' : 'Expand Table'}`}>
                        <span>
                            <IconButton
                                onClick={(): void => {
                                    toggleExpansion(!expanded);
                                }}
                                className={classes.headerIcon}
                                disabled={adjusting}
                                size={isMdOrSmaller ? 'small' : 'large'}
                            >
                                {expanded ? <Icon path={isMdOrSmaller ? mdiViewList : mdiMap} size={1} /> : <Icon path={mdiViewComfy} size={1} />}
                            </IconButton>
                        </span>
                    </Tooltip>
                }
            </span>
            <Divider className={classes.divider} />
            <Grid
                container
                sx={{
                    alignItems: 'center',
                }}
            >
                <Grid item xs='auto'>
                    <Typography variant='subtitle1'>
                        {
                            loading ? `Loading ${title}...` : `${itemCount} ${title}`
                        }
                    </Typography>
                </Grid>
                <Grid item xs className={classes.appliedFiltersContainer}>
                    <ActiveFilters />
                </Grid>
            </Grid>
        </StyledDiv>
    );
};

export default TableHeader;
